/* Vague 92 — Productivity helpers for the company detail page.
   All shortcuts/quick-actions live here so EntrepriseDetail.jsx stays readable. */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, Receipt, UserPlus, CheckSquare, StickyNote, Phone, Mail, Calendar,
  Keyboard, X, Upload, Download, Trash2, FileType2, Image as ImgIcon, Loader2, Pencil,
  AlertTriangle, Check, History, ChevronRight, Sparkles, Zap, Send, Paperclip,
  PenTool, FileSignature, ClipboardList, Search, Clock, CalendarClock,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { sanitizeRichHtml } from '../lib/sanitize';

/* =====================================================================
   A. KEYBOARD SHORTCUTS HOOK
   ===================================================================== */
export function useKeyboardShortcuts(handlers, deps = []) {
  // handlers: { 'n': fn, 't': fn, 'mod+s': fn, '?': fn, 'esc': fn, ... }
  // Ignored when typing in inputs/textareas/contentEditable.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const editable = e.target?.isContentEditable;
      if (editable || tag === 'input' || tag === 'textarea' || tag === 'select') {
        // Allow Cmd/Ctrl+Enter to submit forms even from inside an input
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && handlers['mod+enter']) {
          e.preventDefault();
          handlers['mod+enter'](e);
        }
        if (e.key === 'Escape' && handlers['esc']) {
          handlers['esc'](e);
        }
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      let k = e.key.toLowerCase();
      if (k === 'escape') k = 'esc';
      const combo = mod ? `mod+${k}` : k;
      const fn = handlers[combo] || handlers[k];
      if (fn) {
        e.preventDefault();
        fn(e);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* =====================================================================
   B. SHORTCUTS CHEATSHEET OVERLAY
   ===================================================================== */
const SHORTCUT_GROUPS = [
  {
    title: 'Activités CRM',
    items: [
      ['N', 'Nouvelle note'],
      ['T', 'Nouvelle tâche'],
      ['C', 'Nouvel appel'],
      ['E', 'Nouvel email'],
      ['R', 'Nouveau RDV'],
    ],
  },
  {
    title: 'Documents commerciaux',
    items: [
      ['D', 'Nouveau devis'],
      ['F', 'Facture groupée'],
      ['A', 'Nouvel apprenant'],
    ],
  },
  {
    title: 'Navigation',
    items: [
      ['/', 'Focus zone de saisie rapide'],
      ['J / K', 'Fiches récentes (préc / suiv)'],
      ['Esc', 'Fermer le dialog en cours'],
      ['?', 'Afficher cette aide'],
    ],
  },
  {
    title: 'Édition',
    items: [
      ['⌘/Ctrl+Enter', 'Soumettre le formulaire'],
      ['Double-clic', 'Édition inline du header'],
      ['/task ...', 'Crée une tâche depuis le titre'],
    ],
  },
];

export function ShortcutsOverlay({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="shortcuts-overlay" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-5 border-b border-[#EFF0F6] flex items-center gap-2">
          <Keyboard className="w-5 h-5 text-[#4F46E5]" />
          <h3 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Raccourcis clavier</h3>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg hover:bg-[#FAFAFE] flex items-center justify-center"><X className="w-4 h-4 text-[#6B6C85]" /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-4">
          {SHORTCUT_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#8E8FA5] mb-2">{g.title}</div>
              <div className="space-y-1">
                {g.items.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between gap-3 py-1">
                    <span className="text-[12.5px] text-[#1A1B2E]">{label}</span>
                    <kbd className="px-2 py-0.5 rounded bg-[#EEF2FF] border border-[#C7D2FE] text-[#4F46E5] text-[11px] font-bold font-mono whitespace-nowrap">{k}</kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-[#EFF0F6] text-[11px] text-[#8E8FA5] text-center">
          Les raccourcis sont désactivés quand vous saisissez du texte. Tapez <kbd className="px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] font-bold">?</kbd> n'importe où pour rouvrir cette aide.
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   C. HEADER ACTION MENU — bouton "+ Action rapide" intégré dans le header
   ===================================================================== */
export function HeaderActionMenu({ onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const ITEMS = [
    { key: 'note',     icon: StickyNote,  label: 'Note',         shortcut: 'N',  color: '#4F46E5' },
    { key: 'task',     icon: CheckSquare, label: 'Tâche',        shortcut: 'T',  color: '#EC4899' },
    { key: 'call',     icon: Phone,       label: 'Appel',        shortcut: 'C',  color: '#14B8A6' },
    { key: 'email',    icon: Mail,        label: 'Email',        shortcut: 'E',  color: '#3B82F6' },
    { key: 'meeting',  icon: Calendar,    label: 'RDV',          shortcut: 'R',  color: '#F59E0B' },
    { key: 'quote',    icon: FileText,    label: 'Devis',        shortcut: 'D',  color: '#4F46E5' },
    { key: 'invoice',  icon: Receipt,     label: 'Facture',      shortcut: 'F',  color: '#0F766E' },
    { key: 'learner',  icon: UserPlus,    label: 'Apprenant',    shortcut: 'A',  color: '#3B82F6' },
  ];
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="header-action-menu"
        onClick={() => setOpen((v) => !v)}
        className={`h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 shadow-sm transition-transform ${open ? 'scale-95' : ''}`}
      >
        <Plus className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-45' : ''}`} /> Action rapide
      </button>
      {open && (
        <div data-testid="header-action-menu-list"
          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#EFF0F6] overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {ITEMS.map((it, i) => {
            const Ic = it.icon;
            return (
              <button
                key={it.key}
                data-testid={`header-action-${it.key}`}
                onClick={() => { setOpen(false); onAction(it.key); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#EEF2FF] transition-colors ${i > 0 ? 'border-t border-[#F3F4F8]' : ''}`}
              >
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${it.color}15` }}>
                  <Ic className="w-3.5 h-3.5" style={{ color: it.color }} />
                </div>
                <span className="text-[12.5px] font-semibold text-[#1A1B2E] flex-1">{it.label}</span>
                <kbd className="text-[10px] font-bold text-[#8E8FA5] bg-[#F6F7FB] px-1.5 py-0.5 rounded">{it.shortcut}</kbd>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =====================================================================
   C'. (legacy) FLOATING ACTION BUTTON — kept for backward compat but unused on EntrepriseDetail
   ===================================================================== */
export function QuickActionsFAB({ onAction }) {
  const [open, setOpen] = useState(false);
  const ITEMS = [
    { key: 'note',     icon: StickyNote,  label: 'Note',         color: '#4F46E5' },
    { key: 'task',     icon: CheckSquare, label: 'Tâche',        color: '#EC4899' },
    { key: 'call',     icon: Phone,       label: 'Appel',        color: '#14B8A6' },
    { key: 'email',    icon: Mail,        label: 'Email',        color: '#3B82F6' },
    { key: 'meeting',  icon: Calendar,    label: 'RDV',          color: '#F59E0B' },
    { key: 'quote',    icon: FileText,    label: 'Devis',        color: '#4F46E5' },
    { key: 'invoice',  icon: Receipt,     label: 'Facture',      color: '#0F766E' },
    { key: 'learner',  icon: UserPlus,    label: 'Apprenant',    color: '#3B82F6' },
  ];
  return (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-2" data-testid="quick-fab">
      {open && (
        <div className="flex flex-col-reverse gap-1.5 mb-1 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {ITEMS.map((it) => {
            const Ic = it.icon;
            return (
              <button
                key={it.key}
                data-testid={`fab-${it.key}`}
                onClick={() => { onAction(it.key); setOpen(false); }}
                className="group flex items-center gap-2 pl-3 pr-4 h-10 rounded-full bg-white shadow-lg border border-[#EFF0F6] hover:border-[#C7D2FE] text-[12.5px] font-semibold text-[#1A1B2E] transition-all hover:-translate-x-1"
              >
                <Ic className="w-3.5 h-3.5" style={{ color: it.color }} />
                {it.label}
              </button>
            );
          })}
        </div>
      )}
      <button
        data-testid="fab-toggle"
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full brand-gradient text-white shadow-lg flex items-center justify-center transition-transform ${open ? 'rotate-45' : ''}`}
        title="Actions rapides"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}

/* =====================================================================
   D. INLINE EDIT FIELD
   ===================================================================== */
export function InlineEditField({ value, onSave, type = 'text', placeholder = '—', icon: Icon, className = '', testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);
  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.select(), 30); }, [editing]);

  const commit = async () => {
    if ((draft || '') === (value || '')) { setEditing(false); return; }
    try { await onSave(draft); setEditing(false); }
    catch { toast({ title: 'Erreur', description: 'Sauvegarde impossible', variant: 'destructive' }); }
  };

  if (editing) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        {Icon && <Icon className="w-3.5 h-3.5 text-[#4F46E5]" />}
        <input
          ref={inputRef}
          data-testid={testId}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
          placeholder={placeholder}
          className="h-7 px-2 rounded border border-[#4F46E5] bg-white text-[12.5px] focus:outline-none min-w-[180px]"
          autoFocus
        />
      </span>
    );
  }
  return (
    <span
      onDoubleClick={() => setEditing(true)}
      data-testid={testId ? `${testId}-display` : undefined}
      className={`inline-flex items-center gap-1.5 group cursor-pointer hover:bg-[#EEF2FF] rounded px-1 -mx-1 transition-colors ${className}`}
      title="Double-cliquez pour modifier"
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span className={value ? '' : 'italic text-[#8E8FA5]'}>{value || placeholder}</span>
      <Pencil className="w-3 h-3 text-[#8E8FA5] opacity-0 group-hover:opacity-100 transition-opacity" />
    </span>
  );
}

/* =====================================================================
   E. EXPRESS LEARNER FORM (mini 3-fields inline)
   ===================================================================== */
export function ExpressLearnerForm({ companyId, companyName, onCreated }) {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const reset = () => { setFirst(''); setLast(''); setEmail(''); };
  const submit = async () => {
    if (!first.trim() || !last.trim() || !email.trim()) {
      toast({ title: 'Tous les champs sont requis', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/learners', {
        firstName: first.trim(), lastName: last.trim(), email: email.trim(),
        company_id: companyId, company: companyName,
        tag: 'Entreprise', status: 'Actif',
      });
      toast({ title: 'Apprenant créé ✓', description: `${data.firstName} ${data.lastName}` });
      reset();
      onCreated?.(data);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button data-testid="express-learner-open" onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-1.5 h-10 rounded-xl border-2 border-dashed border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold hover:bg-[#EEF2FF]">
        <UserPlus className="w-3.5 h-3.5" /> Création express d'apprenant (Prénom · Nom · Email)
      </button>
    );
  }
  return (
    <div className="rounded-xl border-2 border-[#4F46E5] bg-[#EEF2FF] p-3" data-testid="express-learner-form">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.4fr_auto_auto] gap-2 items-center">
        <input data-testid="express-firstname" placeholder="Prénom" value={first} onChange={(e) => setFirst(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] outline-none"
          onKeyDown={(e) => e.key === 'Enter' && submit()} autoFocus />
        <input data-testid="express-lastname" placeholder="Nom" value={last} onChange={(e) => setLast(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] outline-none"
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
        <input data-testid="express-email" placeholder="email@exemple.fr" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] outline-none"
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
        <button data-testid="express-submit" onClick={submit} disabled={saving}
          className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
        </button>
        <button onClick={() => { reset(); setOpen(false); }} className="w-9 h-9 rounded-lg hover:bg-white text-[#6B6C85]"><X className="w-3.5 h-3.5 mx-auto" /></button>
      </div>
    </div>
  );
}

/* =====================================================================
   B'. QUICK TASK TEMPLATES (Vague 92 + Vague 93 customizable)
   ===================================================================== */
function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
const DEFAULT_TEMPLATES = [
  { key: 'relance_paiement',  label: 'Relancer paiement',     iconKey: 'receipt', color: '#EF4444', days: 7,  type: 'task',  content: 'Vérifier paiement des factures en attente.' },
  { key: 'rappel_3j',         label: 'Rappel J+3',            iconKey: 'check',   color: '#F59E0B', days: 3,  type: 'task',  content: '' },
  { key: 'preparer_convention',label: 'Préparer convention',  iconKey: 'file',    color: '#4F46E5', days: 2,  type: 'task',  content: 'Préparer la convention de formation et la transmettre au client.' },
  { key: 'envoyer_qualiopi',  label: 'Envoyer docs Qualiopi', iconKey: 'mail',    color: '#0F766E', days: 1,  type: 'email', content: 'Envoyer les pièces Qualiopi (programme, CV formateur, attestation).' },
  { key: 'demande_signature', label: 'Demande de signature',  iconKey: 'pencil',  color: '#3B82F6', days: 1,  type: 'task',  content: 'Relancer le client pour la signature de la convention.' },
];
const ICON_MAP = {
  receipt: Receipt, check: CheckSquare, file: FileText, mail: Mail, pencil: Pencil,
  phone: Phone, calendar: Calendar, sticky: StickyNote, sparkles: Sparkles, zap: Zap, plus: Plus,
};
const ICON_OPTIONS = [
  { key: 'receipt',  label: 'Facture' },
  { key: 'check',    label: 'Tâche' },
  { key: 'file',     label: 'Document' },
  { key: 'mail',     label: 'Email' },
  { key: 'phone',    label: 'Appel' },
  { key: 'calendar', label: 'RDV' },
  { key: 'pencil',   label: 'Crayon' },
  { key: 'sticky',   label: 'Note' },
  { key: 'sparkles', label: 'Étoile' },
  { key: 'zap',      label: 'Éclair' },
];
const TYPE_OPTIONS = [
  { key: 'task',    label: 'Tâche' },
  { key: 'note',    label: 'Note' },
  { key: 'call',    label: 'Appel' },
  { key: 'email',   label: 'Email' },
  { key: 'meeting', label: 'RDV' },
];

const TPL_STORAGE_KEY = 'ifaap_quick_templates';

function loadTemplates() {
  try {
    const raw = localStorage.getItem(TPL_STORAGE_KEY);
    if (!raw) return DEFAULT_TEMPLATES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_TEMPLATES;
    return parsed;
  } catch { return DEFAULT_TEMPLATES; }
}
function saveTemplates(list) {
  localStorage.setItem(TPL_STORAGE_KEY, JSON.stringify(list));
}

export function QuickTaskTemplates({ companyId, onCreated }) {
  const [busy, setBusy] = useState(null);
  const [templates, setTemplates] = useState(loadTemplates());
  const [editorOpen, setEditorOpen] = useState(false);

  const create = async (tpl) => {
    setBusy(tpl.key);
    try {
      const includeDue = tpl.type === 'task' || tpl.type === 'meeting';
      const { data } = await api.post(`/companies/${companyId}/activities`, {
        type: tpl.type,
        title: tpl.label,
        content: tpl.content,
        due_date: includeDue ? addDays(tpl.days) : null,
      });
      toast({ title: tpl.label, description: includeDue ? `Planifiée pour le ${addDays(tpl.days)}` : 'Activité créée' });
      onCreated?.(data);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const onSaved = (next) => { setTemplates(next); saveTemplates(next); setEditorOpen(false); };

  return (
    <>
      <div className="rounded-xl border border-[#C7D2FE] bg-gradient-to-br from-[#EEF2FF] to-[#FAFAFE] p-3" data-testid="task-templates">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#4F46E5]">
            <Zap className="w-3.5 h-3.5" /> Tâches rapides en 1 clic
          </div>
          <button
            data-testid="task-templates-edit"
            onClick={() => setEditorOpen(true)}
            className="text-[11px] text-[#4F46E5] font-semibold hover:underline flex items-center gap-1"
            title="Personnaliser mes raccourcis"
          >
            <Pencil className="w-3 h-3" /> Modifier
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {templates.map((t) => {
            const Ic = ICON_MAP[t.iconKey] || CheckSquare;
            return (
              <button
                key={t.key}
                data-testid={`tpl-${t.key}`}
                disabled={busy === t.key}
                onClick={() => create(t)}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-[#EFF0F6] hover:border-[#4F46E5] text-[11.5px] font-semibold text-[#1A1B2E] transition-colors disabled:opacity-60"
                title={t.content || t.label}
              >
                {busy === t.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ic className="w-3 h-3" style={{ color: t.color }} />}
                {t.label}
              </button>
            );
          })}
          {templates.length === 0 && (
            <span className="text-[11.5px] text-[#8E8FA5] italic">Aucun raccourci. Cliquez sur « Modifier » pour en créer.</span>
          )}
        </div>
      </div>
      <TemplatesEditor
        open={editorOpen}
        templates={templates}
        onClose={() => setEditorOpen(false)}
        onSaved={onSaved}
      />
    </>
  );
}

function TemplatesEditor({ open, templates, onClose, onSaved }) {
  const [list, setList] = useState(templates);
  useEffect(() => { setList(templates); }, [templates, open]);
  if (!open) return null;

  const update = (i, patch) => setList((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (i) => setList((p) => p.filter((_, j) => j !== i));
  const add = () => setList((p) => [
    ...p,
    { key: `custom_${Date.now()}`, label: 'Nouveau raccourci', iconKey: 'check', color: '#4F46E5', days: 1, type: 'task', content: '' },
  ]);
  const move = (i, dir) => setList((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const a = [...p]; const tmp = a[i]; a[i] = a[j]; a[j] = tmp; return a;
  });
  const reset = () => setList(DEFAULT_TEMPLATES);

  const valid = list.every((t) => t.label.trim() && t.type && Number.isFinite(parseInt(t.days, 10)));

  return (
    <div className="fixed inset-0 z-[110] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="templates-editor" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[760px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-5 border-b border-[#EFF0F6] flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#4F46E5]" />
          <div>
            <h3 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Personnaliser mes raccourcis</h3>
            <p className="text-[11.5px] text-[#6B6C85]">Vos raccourcis apparaîtront sur toutes les fiches entreprise. Sauvegardé localement par utilisateur.</p>
          </div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg hover:bg-[#FAFAFE] flex items-center justify-center"><X className="w-4 h-4 text-[#6B6C85]" /></button>
        </div>
        <div className="p-5 space-y-2.5">
          {list.map((t, i) => {
            const Ic = ICON_MAP[t.iconKey] || CheckSquare;
            return (
              <div key={t.key} data-testid={`tpl-edit-${i}`} className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3">
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#FFFFFF', border: '1px solid #EFF0F6' }}>
                      <Ic className="w-4 h-4" style={{ color: t.color }} />
                    </div>
                    <input data-testid={`tpl-${i}-label`} value={t.label} onChange={(e) => update(i, { label: e.target.value })}
                      placeholder="Libellé" className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px] font-semibold focus:border-[#4F46E5] outline-none" />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <select data-testid={`tpl-${i}-type`} value={t.type} onChange={(e) => update(i, { type: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]">
                      {TYPE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <select data-testid={`tpl-${i}-icon`} value={t.iconKey} onChange={(e) => update(i, { iconKey: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]">
                      {ICON_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-6 md:col-span-2 flex items-center gap-1.5">
                    <span className="text-[11px] text-[#6B6C85] whitespace-nowrap">J +</span>
                    <input data-testid={`tpl-${i}-days`} type="number" min="0" max="90" value={t.days} onChange={(e) => update(i, { days: parseInt(e.target.value) || 0 })}
                      className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px] text-center" />
                  </div>
                  <div className="col-span-6 md:col-span-2 flex items-center gap-1">
                    <input data-testid={`tpl-${i}-color`} type="color" value={t.color} onChange={(e) => update(i, { color: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-[#EFF0F6] cursor-pointer" />
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-9 rounded-lg hover:bg-white text-[#6B6C85] disabled:opacity-30">↑</button>
                    <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="w-7 h-9 rounded-lg hover:bg-white text-[#6B6C85] disabled:opacity-30">↓</button>
                    <button data-testid={`tpl-${i}-remove`} onClick={() => remove(i)} className="w-7 h-9 rounded-lg hover:bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="col-span-12 md:col-span-1" />
                  <textarea data-testid={`tpl-${i}-content`} value={t.content} onChange={(e) => update(i, { content: e.target.value })}
                    placeholder="Description pré-remplie (facultatif)" rows={1}
                    className="col-span-12 mt-1 px-2 py-1.5 rounded-lg border border-[#EFF0F6] bg-white text-[12px] resize-none focus:border-[#4F46E5] outline-none" />
                </div>
              </div>
            );
          })}
          <button data-testid="tpl-add" onClick={add}
            className="w-full h-10 rounded-xl border-2 border-dashed border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold hover:bg-[#EEF2FF] flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Ajouter un raccourci
          </button>
        </div>
        <div className="px-5 py-4 border-t border-[#EFF0F6] flex items-center justify-between gap-2">
          <button data-testid="tpl-reset" onClick={reset} className="text-[12px] text-[#6B6C85] hover:text-[#1A1B2E] hover:underline">↻ Restaurer les raccourcis par défaut</button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-lg border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63]">Annuler</button>
            <button data-testid="tpl-save" onClick={() => onSaved(list)} disabled={!valid}
              className="h-9 px-5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   F. SLASH-COMMAND PARSER
   ===================================================================== */
const SLASH_MAP = {
  '/note': 'note', '/notes': 'note',
  '/task': 'task', '/todo': 'task', '/tache': 'task', '/tâche': 'task',
  '/call': 'call', '/appel': 'call',
  '/email': 'email', '/mail': 'email',
  '/meeting': 'meeting', '/rdv': 'meeting', '/reunion': 'meeting',
};
export function parseSlashCommand(input) {
  const s = (input || '').trim();
  if (!s.startsWith('/')) return null;
  const sp = s.indexOf(' ');
  // Require verb + space + at least some content; no fallback to capitalised verb.
  if (sp <= 0) return null;
  const prefix = s.slice(0, sp).toLowerCase();
  const type = SLASH_MAP[prefix];
  if (!type) return null;
  const rest = s.slice(sp + 1).trim();
  if (!rest) return null;
  // extract due-date hints like "+7j" "+3d" "demain"
  let due = null;
  let title = rest;
  const dueMatch = rest.match(/\s*\+(\d+)\s*([jd])\s*$/i);
  if (dueMatch) {
    due = addDays(parseInt(dueMatch[1], 10));
    title = rest.replace(dueMatch[0], '').trim();
  } else if (/\s+demain\s*$/i.test(rest)) {
    due = addDays(1);
    title = rest.replace(/\s+demain\s*$/i, '').trim();
  }
  return { type, title, due };
}

/* =====================================================================
   G. SMART PASTE (SIRET / EMAIL detection)
   ===================================================================== */
export function detectSmartPaste(text) {
  if (!text) return null;
  const t = text.trim();
  // SIRET = 14 digits possibly with spaces
  const digits = t.replace(/\s+/g, '');
  if (/^\d{14}$/.test(digits)) return { kind: 'siret', value: digits };
  if (/^\d{9}$/.test(digits)) return { kind: 'siren', value: digits };
  // Email
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return { kind: 'email', value: t };
  return null;
}

/* =====================================================================
   H. RECENT COMPANIES NAV (localStorage based)
   ===================================================================== */
const RECENT_KEY = 'ifaap_recent_companies';
const MAX_RECENT = 5;

export function pushRecentCompany(c) {
  if (!c || !c.id) return;
  try {
    const cur = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    const filtered = cur.filter((x) => x.id !== c.id);
    filtered.unshift({ id: c.id, name: c.name, opco_code: c.opco_code });
    localStorage.setItem(RECENT_KEY, JSON.stringify(filtered.slice(0, MAX_RECENT)));
  } catch { /* ignore */ }
}

export function getRecentCompanies() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
  catch { return []; }
}

export function RecentCompaniesNav({ currentId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { setItems(getRecentCompanies()); }, [currentId]);
  if (!items.length || items.length === 1) return null;
  return (
    <div className="flex items-center gap-2 text-[11.5px] flex-wrap" data-testid="recent-companies-nav">
      <span className="inline-flex items-center gap-1 text-[#8E8FA5] font-semibold uppercase tracking-wide text-[10.5px]">
        <History className="w-3 h-3" /> Récentes
      </span>
      {items.map((it) => {
        const active = it.id === currentId;
        return (
          <button
            key={it.id}
            data-testid={`recent-${it.id}`}
            onClick={() => navigate(`/app/entreprises/${it.id}`)}
            className={`px-2 h-7 rounded-md font-semibold transition-colors ${active ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-white border border-[#EFF0F6] text-[#4A4B63] hover:border-[#C7D2FE] hover:text-[#4F46E5]'}`}
          >
            {it.name?.slice(0, 22) || it.id}
            {it.opco_code && <span className="ml-1 text-[9.5px] text-[#0F766E]">{it.opco_code}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* =====================================================================
   I. DOCUMENTS TAB (drag & drop, list, preview, delete)
   ===================================================================== */
const FILE_ICONS = {
  pdf: FileType2,
  doc: FileType2, docx: FileType2,
  xls: FileType2, xlsx: FileType2,
  ppt: FileType2, pptx: FileType2,
  png: ImgIcon, jpg: ImgIcon, jpeg: ImgIcon, webp: ImgIcon, gif: ImgIcon,
};
function fileIcon(filename, contentType) {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  return FILE_ICONS[ext] || (contentType?.startsWith('image/') ? ImgIcon : FileType2);
}
function fmtBytes(n) {
  if (!n) return '0 o';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export function DocumentsTab({ companyId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/documents`);
      setDocs(data || []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);

  const upload = async (file, label) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Trop volumineux', description: 'Taille maximale : 5 Mo', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (label) fd.append('label', label);
      const { data } = await api.post(`/companies/${companyId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocs((p) => [data, ...p]);
      toast({ title: 'Document ajouté ✓', description: file.name });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Upload impossible', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach((f) => upload(f));
  };

  const remove = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.filename}" ?`)) return;
    try {
      await api.delete(`/companies/${companyId}/documents/${doc.id}`);
      setDocs((p) => p.filter((d) => d.id !== doc.id));
      toast({ title: 'Document supprimé' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible', variant: 'destructive' });
    }
  };

  const downloadUrl = (doc) => `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/documents/${doc.id}`;

  return (
    <div className="space-y-4" data-testid="documents-tab">
      {/* Drop zone */}
      <div
        data-testid="documents-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#C7D2FE] bg-[#FAFAFE] hover:border-[#4F46E5]'}`}
      >
        <Upload className="w-10 h-10 mx-auto text-[#4F46E5] mb-2" />
        <div className="text-[14px] font-bold text-[#1A1B2E]">Glissez-déposez vos fichiers ici</div>
        <div className="text-[12px] text-[#6B6C85] mt-1">ou cliquez pour parcourir · PDF, Word, Excel, PowerPoint, images · 5 Mo max par fichier</div>
        <input
          ref={fileInputRef}
          type="file"
          data-testid="documents-file-input"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            files.forEach((f) => upload(f));
            e.target.value = '';
          }}
        />
        {uploading && <div className="text-[12px] text-[#4F46E5] mt-2 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Upload en cours…</div>}
      </div>

      {/* List */}
      <div className="soft-card p-5">
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Documents ({docs.length})</h3>
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline text-[#4F46E5]" /></div>
        ) : docs.length === 0 ? (
          <div className="text-center py-6 text-[12.5px] text-[#8E8FA5] italic">Aucun document. Déposez votre premier fichier ci-dessus.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {docs.map((d) => {
              const Ic = fileIcon(d.filename, d.content_type);
              const isImage = (d.content_type || '').startsWith('image/');
              return (
                <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#EFF0F6] hover:border-[#C7D2FE] bg-white">
                  <div className="w-10 h-10 rounded-lg bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center shrink-0">
                    <Ic className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{d.filename}</div>
                    <div className="text-[10.5px] text-[#8E8FA5] mt-0.5">
                      {fmtBytes(d.size)} · {d.uploaded_by} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('fr-FR') : '—'}
                    </div>
                  </div>
                  <a href={downloadUrl(d)} target={isImage ? '_blank' : '_self'} rel="noreferrer" download={d.filename} title="Télécharger"
                    className="w-8 h-8 rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF] flex items-center justify-center">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button data-testid={`doc-${d.id}-delete`} onClick={() => remove(d)} title="Supprimer"
                    className="w-8 h-8 rounded-lg hover:bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
   J. EMAIL COMPOSER — Vague 96 (Phase 1+2+3: signature, PJ, templates,
   devis, brouillons auto-save, rich-text, threading reply)
   ===================================================================== */

// Strip HTML tags + decode entities for plain-text fallback used in
// the body placeholder when restoring drafts saved as plain text.
const _STRIP_RE = /<[^>]+>/g;
const stripHtml = (s) => (s || '').replace(_STRIP_RE, '').trim();

const DRAFT_KEY = (cid) => `ifaap_email_draft_${cid}`;

export function EmailComposer({ open, onClose, company, defaultTo, onSent, replyTo = null }) {
  const [to, setTo] = useState(defaultTo || '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(''); // HTML string (rich text)
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  // Phase-1 state
  const [useSignature, setUseSignature] = useState(true);
  const [attachments, setAttachments] = useState([]); // [{filename, content_type, content_b64, _size}]
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [companyQuotes, setCompanyQuotes] = useState([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [showQuotePicker, setShowQuotePicker] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState('');
  // Phase-2 state
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Phase-3 state — thread / reply context
  const threadId = replyTo?.metadata?.thread_id || null;
  // Vague 96.8 — scheduled send
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(''); // 'YYYY-MM-DD'
  const [scheduleTime, setScheduleTime] = useState(''); // 'HH:MM'
  const [scheduling, setScheduling] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  // Reset on open + handle reply mode
  useEffect(() => {
    if (!open) return;
    if (replyTo) {
      // Reply mode: pre-fill from the original activity
      const meta = replyTo.metadata || {};
      const origTo = meta.to || '';
      const origSubject = replyTo.title || '';
      const origPlain = stripHtml(replyTo.content || '');
      const dateStr = new Date(replyTo.created_at).toLocaleString('fr-FR');
      const sender = replyTo.created_by_name || replyTo.created_by || 'expéditeur';
      const quoted = `<br><br><blockquote style="border-left:3px solid #EFF0F6;margin:0;padding:6px 12px;color:#6B6C85;font-size:12.5px"><div><em>Le ${dateStr}, ${sender} a écrit :</em></div><div style="margin-top:4px;white-space:pre-wrap">${origPlain.slice(0, 1200)}</div></blockquote>`;
      setTo(origTo || defaultTo || '');
      setCc(meta.cc || '');
      setBcc('');
      setSubject(origSubject.startsWith('Re:') ? origSubject : `Re: ${origSubject}`);
      setBody(`<p><br></p>${quoted}`);
      setShowCc(!!meta.cc);
      setShowBcc(false);
      setUseSignature(true);
      setAttachments([]);
      setSelectedQuoteId('');
      setShowTemplates(false);
      setShowQuotePicker(false);
      setDraftRestored(false);
      return;
    }
    // Normal open: try restore draft from localStorage
    let restored = false;
    try {
      const raw = localStorage.getItem(DRAFT_KEY(company?.id || ''));
      if (raw) {
        const d = JSON.parse(raw);
        if (d && (d.subject || d.body || d.to)) {
          setTo(d.to || defaultTo || '');
          setCc(d.cc || ''); setBcc(d.bcc || '');
          setSubject(d.subject || ''); setBody(d.body || '');
          setShowCc(!!d.cc); setShowBcc(!!d.bcc);
          setUseSignature(d.use_signature !== false);
          setSelectedQuoteId(d.quote_id || '');
          restored = true;
        }
      }
    } catch { /* ignore */ }
    if (!restored) {
      setTo(defaultTo || '');
      setCc(''); setBcc('');
      setSubject(''); setBody('');
      setShowCc(false); setShowBcc(false);
      setUseSignature(true);
      setSelectedQuoteId('');
    }
    setAttachments([]);
    setShowTemplates(false);
    setShowQuotePicker(false);
    setDraftRestored(restored);
  }, [open, defaultTo, company?.id, replyTo]);

  // Sync editor HTML <- body when body changes externally (template apply, restore)
  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== body) {
      editorRef.current.innerHTML = body || '';
    }
  }, [body, open]);

  // Load templates + signature + company quotes
  useEffect(() => {
    if (!open || !company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [tplRes, sigRes, dashRes] = await Promise.all([
          api.get('/user/email-templates').catch(() => ({ data: [] })),
          api.get('/user/signature').catch(() => ({ data: { signature_html: '' } })),
          api.get(`/companies/${company.id}/dashboard`).catch(() => ({ data: { recent_quotes: [] } })),
        ]);
        if (cancelled) return;
        setTemplates(tplRes.data || []);
        setSignatureHtml(sigRes.data?.signature_html || '');
        setCompanyQuotes(dashRes.data?.recent_quotes || []);
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [open, company?.id]);

  // Auto-save draft (debounced 1.5s) — Phase 2
  useEffect(() => {
    if (!open || !company?.id || replyTo) return;
    const isEmpty = !to.trim() && !subject.trim() && !stripHtml(body);
    if (isEmpty) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY(company.id), JSON.stringify({
          to, cc, bcc, subject, body,
          use_signature: useSignature,
          quote_id: selectedQuoteId,
          updated_at: Date.now(),
        }));
        setDraftSavedAt(new Date());
      } catch {/* localStorage quota */}
    }, 1500);
    return () => clearTimeout(t);
  }, [open, company?.id, replyTo, to, cc, bcc, subject, body, useSignature, selectedQuoteId]);

  if (!open || !company) return null;

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY(company.id)); } catch {/* ignore */}
    setDraftRestored(false);
    setDraftSavedAt(null);
  };

  const discardDraft = () => {
    clearDraft();
    setTo(defaultTo || ''); setCc(''); setBcc('');
    setSubject(''); setBody('');
    setShowCc(false); setShowBcc(false);
    setUseSignature(true);
    setSelectedQuoteId('');
    setAttachments([]);
    toast({ title: 'Brouillon supprimé' });
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body_html || '');
    setShowTemplates(false);
    toast({ title: 'Modèle appliqué', description: tpl.name });
  };

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) {
        toast({ title: 'Fichier trop volumineux', description: `${f.name} (max 8 Mo)`, variant: 'destructive' });
        continue;
      }
      const b64 = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result || '').toString().split(',')[1] || '');
        r.readAsDataURL(f);
      });
      setAttachments((prev) => [...prev, {
        filename: f.name,
        content_type: f.type || 'application/octet-stream',
        content_b64: b64,
        _size: f.size,
      }]);
    }
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // Rich text commands — Phase 3
  const exec = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      setBody(editorRef.current.innerHTML);
    }
  };
  const insertLink = () => {
    const url = window.prompt('URL du lien :', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const submit = async () => {
    if (!to || !to.includes('@')) { toast({ title: 'Adresse destinataire invalide', variant: 'destructive' }); return; }
    if (!subject.trim()) { toast({ title: 'Sujet obligatoire', variant: 'destructive' }); return; }
    if (!stripHtml(body)) { toast({ title: 'Contenu obligatoire', variant: 'destructive' }); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/email`, {
        to: to.trim(),
        cc: cc.trim() || undefined,
        bcc: bcc.trim() || undefined,
        subject: subject.trim(),
        body_html: body,
        use_signature: useSignature,
        attachments: attachments.map(({ filename, content_type, content_b64 }) => ({
          filename, content_type, content_b64,
        })),
        quote_id: selectedQuoteId || undefined,
        thread_id: threadId || undefined,
      });
      onSent?.(data.activity);
      if (data.ok) {
        toast({ title: 'Email envoyé ✓', description: `À ${to}` });
        clearDraft();
        onClose();
      } else {
        toast({
          title: 'Email non envoyé',
          description: `${data.error || "Erreur d'envoi"} — l'activité a été consignée dans la timeline.`,
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Échec de la requête',
        variant: 'destructive',
      });
    } finally { setSending(false); }
  };

  const fmtSize = (b) => b < 1024 ? `${b} o` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} Ko` : `${(b / (1024 * 1024)).toFixed(1)} Mo`;
  const selectedQuote = companyQuotes.find((q) => q.id === selectedQuoteId);

  // Vague 96.8 — schedule helpers
  const SCHEDULE_PRESETS = [
    { label: 'Demain matin · 9 h', delta: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Demain · 14 h', delta: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(14, 0, 0, 0); return d; } },
    { label: 'Lundi · 9 h', delta: () => { const d = new Date(); const day = d.getDay(); const add = (8 - (day || 7)) % 7 || 7; d.setDate(d.getDate() + add); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Dans 1 heure', delta: () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); return d; } },
  ];

  const applyPreset = (fn) => {
    const d = fn();
    setScheduleDate(d.toISOString().slice(0, 10));
    setScheduleTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
  };

  const submitSchedule = async () => {
    if (!to || !to.includes('@')) { toast({ title: 'Adresse destinataire invalide', variant: 'destructive' }); return; }
    if (!subject.trim()) { toast({ title: 'Sujet obligatoire', variant: 'destructive' }); return; }
    if (!stripHtml(body)) { toast({ title: 'Contenu obligatoire', variant: 'destructive' }); return; }
    if (!scheduleDate || !scheduleTime) { toast({ title: 'Date et heure requises', variant: 'destructive' }); return; }
    const local = new Date(`${scheduleDate}T${scheduleTime}:00`);
    if (Number.isNaN(local.getTime())) { toast({ title: 'Date invalide', variant: 'destructive' }); return; }
    if (local.getTime() <= Date.now() + 30000) { toast({ title: 'Choisissez une date dans le futur (≥ 30 s)', variant: 'destructive' }); return; }
    setScheduling(true);
    try {
      const { data } = await api.post('/scheduled-emails', {
        company_id: company.id,
        to: to.trim(),
        cc: cc.trim() || '',
        bcc: bcc.trim() || '',
        subject: subject.trim(),
        body_html: body,
        use_signature: useSignature,
        attachments: attachments.map(({ filename, content_type, content_b64 }) => ({ filename, content_type, content_b64 })),
        quote_id: selectedQuoteId || null,
        thread_id: threadId || null,
        scheduled_at: local.toISOString(),
      });
      toast({
        title: 'Email programmé ✓',
        description: `Sera envoyé à ${local.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`,
      });
      onSent?.({ scheduled: true, schedule_id: data.id });
      clearDraft();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Programmation impossible', variant: 'destructive' });
    } finally { setScheduling(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="email-composer" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[760px] max-h-[94vh] overflow-y-auto bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#E0F2FE] text-[#3B82F6] flex items-center justify-center"><Mail className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-[#1A1B2E] truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {replyTo ? 'Répondre' : 'Nouvel email'}
              {threadId && <span data-testid="email-thread-badge" className="ml-2 text-[10.5px] font-mono text-[#4F46E5] bg-[#EEF2FF] px-1.5 py-0.5 rounded">{threadId.slice(0, 14)}…</span>}
            </h3>
            <p className="text-[11px] text-[#6B6C85]">Sera consigné automatiquement comme activité dans la fiche {company.name}.</p>
          </div>
          <button data-testid="email-templates-toggle" onClick={() => setShowTemplates((v) => !v)}
            className="h-8 px-2.5 rounded-lg border border-[#EFF0F6] text-[11.5px] font-semibold text-[#4A4B63] hover:bg-[#FAFAFE] flex items-center gap-1.5">
            <ClipboardList className="w-3.5 h-3.5" />
            Modèles ({templates.length})
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#FAFAFE] flex items-center justify-center"><X className="w-4 h-4 text-[#6B6C85]" /></button>
        </div>

        {draftRestored && !replyTo && (
          <div data-testid="email-draft-restored-banner"
            className="px-5 py-2 bg-[#FFF7ED] border-b border-[#FED7AA] flex items-center justify-between">
            <div className="flex items-center gap-2 text-[12px] text-[#9A3412]">
              <History className="w-3.5 h-3.5" />
              Brouillon restauré automatiquement.
            </div>
            <button data-testid="email-draft-discard" onClick={discardDraft}
              className="text-[11px] font-semibold text-[#9A3412] hover:underline">Repartir de zéro</button>
          </div>
        )}

        {showTemplates && (
          <div data-testid="email-templates-panel" className="px-5 py-3 bg-[#FAFAFE] border-b border-[#EFF0F6]">
            <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wide mb-2">Modèles disponibles</div>
            <div className="grid grid-cols-2 gap-2">
              {templates.length === 0 && (
                <div className="text-[12px] text-[#8E8FA5] col-span-2">Aucun modèle. Créez-en dans Paramètres &gt; Modèles email.</div>
              )}
              {templates.map((t) => (
                <button key={t.id} data-testid={`email-template-${t.id}`} onClick={() => applyTemplate(t)}
                  className="text-left p-2.5 rounded-lg border border-[#EFF0F6] bg-white hover:border-[#4F46E5] hover:shadow-sm transition">
                  <div className="text-[12.5px] font-bold text-[#1A1B2E]">{t.name}</div>
                  {t.description && <div className="text-[11px] text-[#6B6C85] mt-0.5 line-clamp-1">{t.description}</div>}
                  <div className="text-[10.5px] text-[#8E8FA5] mt-1 italic line-clamp-1">{t.subject}</div>
                </button>
              ))}
            </div>
            <div className="text-[10.5px] text-[#8E8FA5] mt-2">Variables : <code className="bg-white px-1 rounded">{'{{contact_name}} {{company_name}} {{user_name}} {{quote_id}} {{quote_total}} {{today}}'}</code></div>
          </div>
        )}

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-[60px_1fr] items-center gap-2 border-b border-[#F3F4F8] pb-2">
            <span className="text-[11.5px] font-semibold text-[#8E8FA5]">À</span>
            <div className="flex items-center gap-2">
              <input data-testid="email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="contact@exemple.fr"
                className="flex-1 h-9 px-2 text-[13px] bg-transparent outline-none border-b border-transparent focus:border-[#4F46E5]" />
              {!showCc && (
                <button data-testid="email-cc-toggle" onClick={() => setShowCc(true)}
                  className="text-[11px] text-[#4F46E5] font-semibold hover:underline">Cc</button>
              )}
              {!showBcc && (
                <button data-testid="email-bcc-toggle" onClick={() => setShowBcc(true)}
                  className="text-[11px] text-[#4F46E5] font-semibold hover:underline">Cci</button>
              )}
            </div>
          </div>
          {showCc && (
            <div className="grid grid-cols-[60px_1fr] items-center gap-2 border-b border-[#F3F4F8] pb-2">
              <span className="text-[11.5px] font-semibold text-[#8E8FA5]">Cc</span>
              <input data-testid="email-cc" type="text" value={cc} onChange={(e) => setCc(e.target.value)}
                placeholder="manager@exemple.fr"
                className="h-9 px-2 text-[13px] bg-transparent outline-none border-b border-transparent focus:border-[#4F46E5]" />
            </div>
          )}
          {showBcc && (
            <div className="grid grid-cols-[60px_1fr] items-center gap-2 border-b border-[#F3F4F8] pb-2">
              <span className="text-[11.5px] font-semibold text-[#8E8FA5]">Cci</span>
              <input data-testid="email-bcc" type="text" value={bcc} onChange={(e) => setBcc(e.target.value)}
                placeholder="copie@exemple.fr"
                className="h-9 px-2 text-[13px] bg-transparent outline-none border-b border-transparent focus:border-[#4F46E5]" />
            </div>
          )}
          <div className="grid grid-cols-[60px_1fr] items-center gap-2 border-b border-[#F3F4F8] pb-2">
            <span className="text-[11.5px] font-semibold text-[#8E8FA5]">Sujet</span>
            <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de l'email"
              className="h-9 px-2 text-[13.5px] font-semibold bg-transparent outline-none border-b border-transparent focus:border-[#4F46E5]" />
          </div>

          {/* Rich-text toolbar — Phase 3 */}
          <div data-testid="email-rich-toolbar" className="flex items-center gap-1 px-2 py-1.5 border border-[#EFF0F6] rounded-t-lg bg-[#FAFAFE]">
            <RtBtn testid="rt-bold" title="Gras (Ctrl+B)" onClick={() => exec('bold')}><strong>B</strong></RtBtn>
            <RtBtn testid="rt-italic" title="Italique (Ctrl+I)" onClick={() => exec('italic')}><em>I</em></RtBtn>
            <RtBtn testid="rt-underline" title="Souligné" onClick={() => exec('underline')}><u>U</u></RtBtn>
            <span className="w-px h-4 bg-[#EFF0F6] mx-1" />
            <RtBtn testid="rt-ul" title="Liste à puces" onClick={() => exec('insertUnorderedList')}>•</RtBtn>
            <RtBtn testid="rt-ol" title="Liste numérotée" onClick={() => exec('insertOrderedList')}>1.</RtBtn>
            <span className="w-px h-4 bg-[#EFF0F6] mx-1" />
            <RtBtn testid="rt-link" title="Lien" onClick={insertLink}>🔗</RtBtn>
            <RtBtn testid="rt-clear" title="Effacer le formatage" onClick={() => exec('removeFormat')}>×fmt</RtBtn>
          </div>
          <div
            ref={editorRef}
            data-testid="email-body"
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => setBody(e.currentTarget.innerHTML)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
            className="w-full min-h-[200px] max-h-[400px] overflow-y-auto px-3 py-2 rounded-b-lg border border-t-0 border-[#EFF0F6] text-[13px] focus:border-[#4F46E5] outline-none whitespace-pre-wrap"
            style={{ lineHeight: 1.55 }}
            data-placeholder="Bonjour {{contact_name}},…"
          />

          {/* Signature toggle */}
          <div className="p-2.5 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="email-signature-toggle" type="checkbox" checked={useSignature} onChange={(e) => setUseSignature(e.target.checked)}
                className="w-4 h-4 accent-[#4F46E5]" />
              <PenTool className="w-3.5 h-3.5 text-[#4F46E5]" />
              <span className="text-[12.5px] font-semibold text-[#1A1B2E]">Joindre ma signature</span>
            </label>
            {useSignature && signatureHtml && (
              <div data-testid="email-signature-preview"
                className="mt-2 p-2 bg-white border border-dashed border-[#EFF0F6] rounded-md max-h-[160px] overflow-y-auto text-[11px] text-[#6B6C85]"
                style={{ wordBreak: 'break-word' }}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(signatureHtml) }} className="[&_img]:max-h-[140px] [&_img]:h-auto [&_img]:w-auto" />
              </div>
            )}
          </div>

          {/* Quote attach */}
          <div className="p-2.5 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSignature className="w-3.5 h-3.5 text-[#4F46E5]" />
                <span className="text-[12.5px] font-semibold text-[#1A1B2E]">Joindre un devis</span>
                {selectedQuote && (
                  <span data-testid="email-quote-selected" className="text-[11px] text-[#0F766E] font-bold">· {selectedQuote.id} ({selectedQuote.amount}€)</span>
                )}
              </div>
              <div className="flex gap-2">
                {selectedQuoteId && (
                  <button onClick={() => setSelectedQuoteId('')}
                    className="text-[11px] text-[#EF4444] font-semibold hover:underline">Retirer</button>
                )}
                <button data-testid="email-quote-toggle" onClick={() => setShowQuotePicker((v) => !v)}
                  className="text-[11px] text-[#4F46E5] font-semibold hover:underline">{showQuotePicker ? 'Fermer' : 'Choisir'}</button>
              </div>
            </div>
            {showQuotePicker && (
              <div data-testid="email-quote-picker" className="mt-2 max-h-[140px] overflow-y-auto space-y-1">
                {companyQuotes.length === 0 && (
                  <div className="text-[11px] text-[#8E8FA5] italic">Aucun devis pour cette entreprise.</div>
                )}
                {companyQuotes.map((q) => (
                  <button key={q.id} data-testid={`email-quote-${q.id}`} onClick={() => { setSelectedQuoteId(q.id); setShowQuotePicker(false); }}
                    className={`w-full text-left p-2 rounded border text-[12px] flex items-center justify-between hover:border-[#4F46E5] ${selectedQuoteId === q.id ? 'border-[#4F46E5] bg-[#F3EFFF]' : 'border-[#EFF0F6] bg-white'}`}>
                    <span className="font-mono font-bold text-[#4F46E5]">{q.id}</span>
                    <span className="text-[#6B6C85]">{q.status} · {q.amount}€ · {q.date}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="p-2.5 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip className="w-3.5 h-3.5 text-[#4F46E5]" />
                <span className="text-[12.5px] font-semibold text-[#1A1B2E]">Pièces jointes ({attachments.length})</span>
              </div>
              <button data-testid="email-attach-btn" onClick={() => fileInputRef.current?.click()}
                className="text-[11px] text-[#4F46E5] font-semibold hover:underline flex items-center gap-1">
                <Upload className="w-3 h-3" /> Ajouter un fichier
              </button>
              <input ref={fileInputRef} type="file" multiple onChange={onFileChange}
                data-testid="email-attach-input" className="hidden" />
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((a, idx) => (
                  <div key={idx} data-testid={`email-attachment-${idx}`}
                    className="flex items-center gap-2 text-[12px] bg-white px-2.5 py-1.5 rounded border border-[#EFF0F6]">
                    <FileType2 className="w-3.5 h-3.5 text-[#6B6C85]" />
                    <span className="flex-1 truncate font-semibold text-[#1A1B2E]">{a.filename}</span>
                    <span className="text-[#8E8FA5]">{fmtSize(a._size)}</span>
                    <button data-testid={`email-attachment-remove-${idx}`} onClick={() => removeAttachment(idx)}
                      className="text-[#EF4444] hover:bg-red-50 p-1 rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10.5px] text-[#8E8FA5] mt-1.5">Max 8 Mo par fichier.</div>
          </div>

          <div className="text-[10.5px] text-[#8E8FA5] flex items-center justify-between">
            <span>Astuce : <kbd className="px-1 py-0.5 bg-[#F6F7FB] rounded text-[10px]">⌘/Ctrl + Enter</kbd> pour envoyer.</span>
            {draftSavedAt && (
              <span data-testid="email-draft-saved-at" className="text-[#0F766E] font-semibold">Brouillon enregistré · {draftSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            )}
          </div>
        </div>
        {showSchedule && (
          <div data-testid="email-schedule-panel" className="px-5 py-3 bg-[#FFF7ED] border-t border-[#FED7AA]">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-[#C2410C]" />
              <span className="text-[12.5px] font-bold text-[#9A3412]">Programmer l'envoi</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {SCHEDULE_PRESETS.map((p, i) => (
                <button key={i} data-testid={`schedule-preset-${i}`} onClick={() => applyPreset(p.delta)}
                  className="h-7 px-2 rounded-md bg-white border border-[#FED7AA] text-[11.5px] font-semibold text-[#9A3412] hover:bg-[#FFF7ED]">
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="schedule-date" type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="h-9 px-2 rounded-md border border-[#FED7AA] bg-white text-[12.5px] focus:border-[#C2410C] outline-none" />
              <input data-testid="schedule-time" type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                className="h-9 px-2 rounded-md border border-[#FED7AA] bg-white text-[12.5px] focus:border-[#C2410C] outline-none" />
              <button data-testid="schedule-confirm" onClick={submitSchedule} disabled={scheduling || !scheduleDate || !scheduleTime}
                className="ml-auto h-9 px-4 rounded-md bg-[#C2410C] text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#9A3412]">
                {scheduling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
                Programmer l'envoi
              </button>
            </div>
            <div className="text-[10.5px] text-[#9A3412] mt-1.5">L'email partira automatiquement à cet horaire (fuseau de l'utilisateur). Vous pouvez l'annuler avant l'envoi depuis Paramètres &gt; Emails programmés.</div>
          </div>
        )}
        <div className="px-5 py-4 border-t border-[#EFF0F6] flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:bg-[#FAFAFE]">Annuler</button>
          <button data-testid="email-schedule-toggle" onClick={() => setShowSchedule((v) => !v)}
            className={`h-10 px-3 rounded-lg border text-[12.5px] font-semibold flex items-center gap-1.5 ${showSchedule ? 'bg-[#FFF7ED] border-[#FED7AA] text-[#C2410C]' : 'border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE]'}`}>
            <CalendarClock className="w-3.5 h-3.5" /> {showSchedule ? 'Masquer' : 'Programmer'}
          </button>
          <button data-testid="email-send" onClick={submit} disabled={sending || !to || !subject.trim() || !stripHtml(body)}
            className="h-10 px-5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

function RtBtn({ children, onClick, title, testid }) {
  return (
    <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="h-7 min-w-[28px] px-1.5 rounded text-[12px] font-bold text-[#4A4B63] hover:bg-white hover:text-[#4F46E5] border border-transparent hover:border-[#EFF0F6] transition-colors">
      {children}
    </button>
  );
}

export function useSmartPasteWatcher(onSiret, onEmail) {
  // Listens for clipboard paste events and triggers callbacks if the pasted
  // content looks like a SIRET/SIREN or email.
  useEffect(() => {
    const onPaste = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      // Only intercept when not in form fields (we want to suggest in *header*)
      if (tag === 'input' || tag === 'textarea') return;
      const text = e.clipboardData?.getData('text');
      const r = detectSmartPaste(text);
      if (!r) return;
      if (r.kind === 'siret' || r.kind === 'siren') onSiret?.(r);
      else if (r.kind === 'email') onEmail?.(r);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onSiret, onEmail]);
}
